import React from 'react';

import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

import { TestableCard, TestableChip, TestableTable, TestableTableBody, TestableTableCell, TestableTableHead, TestableTableHeaderCell, TestableTableRow, TestableText } from '@components/general/TestableComponents';
import AvatarLogo from '@components/general/AvatarLogo';
import { getStatusDescription, convertDateToLocal } from "@datahooks/metadata/DataUtils";
import LoadingIcon from '@components/navigation/LoadingIcon';

import PropTypes from 'prop-types';
import StyledTooltip from '@components/general/StyledTooltip';

export default function OrganisationOverviewProjectTable({ projects = [], isLoading = false }) {

  if (isLoading) {
    return (
      <TestableCard dataTestId="org-overview-project-table-card-loading" className="p-4 w-full">
        <div className="flex w-full justify-center">
          <LoadingIcon showText />
        </div>
      </TestableCard>
    );
  }

  return (
    <TestableCard dataTestId="org-overview-project-table-card" className="p-0 text-blue-500">
      <div className="p-4 md:p-5 pb-3">
        <TestableText dataTestId="oopt-title" variant="h3">Project Overview</TestableText>
      </div>

      <TestableTable dataTestId="oopt" >
        <TestableTableHead dataTestId="oopt-head">
          <TestableTableRow dataTestId="oopt-head-row" className="bg-gray-50 dark:bg-gray-800">
            <TestableTableHeaderCell dataTestId="oopt-head-project" variant="lead">Project</TestableTableHeaderCell>
            <TestableTableHeaderCell dataTestId="oopt-head-last-run" variant="lead">Last Run</TestableTableHeaderCell>
            <TestableTableHeaderCell dataTestId="oopt-head-timestamp" variant="lead">Timestamp</TestableTableHeaderCell>
            <TestableTableHeaderCell dataTestId="oopt-head-status" className="flex justify-center" variant="lead">Current Status</TestableTableHeaderCell>
            <TestableTableHeaderCell dataTestId="oopt-head-performance" variant="lead">Tags</TestableTableHeaderCell>
            <TestableTableHeaderCell dataTestId="oopt-head-action" />
          </TestableTableRow>
        </TestableTableHead>

        <TestableTableBody dataTestId="oopt-body" className="divide-gray-300 dark:divide-gray-700">
          {projects.map((project) => {
            
            const latestRelease = project.releases?.[0];

            if (!latestRelease) {
              return null;
            }

            const statusInfo = getStatusDescription(latestRelease.release_status);
            const latestReleaseDate = convertDateToLocal(latestRelease.creation_date, true, true);

            return (
            <TestableTableRow dataTestId={`oopt-row-${project.short_name}`} key={project.short_name} className="border-t border-gray-200 dark:border-gray-700">
              <TestableTableCell dataTestId={`oopt-project-${project.short_name}`}>
                <StyledTooltip content="View project details" placement="top">
                  <Link data-testid={`overview-project-${project.projectId}-title-link`} key={project.short_name} to={`/projects/${project.projectId}/releases/${latestRelease.release_id}`}>
                    <div className="flex items-center gap-3 min-w-[220px]">
                      <AvatarLogo alt={project.short_name} size="sm" displayType="avatar" src={project.logo_location} />
                      <div>
                        <TestableText dataTestId={`oopt-project-name-${project.short_name}`} variant="paragraph">{project.short_name}</TestableText>
                        <TestableText dataTestId={`oopt-project-intro-${project.short_name}`} variant="small" className="truncate max-w-[250px]">{project.intro_text ?? ''}</TestableText>
                      </div>
                    </div>
                  </Link>
                </StyledTooltip>
              </TestableTableCell>
              <TestableTableCell dataTestId={`oopt-last-run-${project.short_name}`}>
                <TestableText dataTestId={`oopt-last-run-version-${project.short_name}`} variant="small">{latestRelease.version_id}</TestableText>
              </TestableTableCell>
              <TestableTableCell dataTestId={`oopt-timestamp-${project.short_name}`}>
                <TestableText dataTestId={`oopt-timestamp-value-${project.short_name}`} variant="small">{latestReleaseDate ? latestReleaseDate.toLocaleString() : 'N/A'}</TestableText>
              </TestableTableCell>
              <TestableTableCell dataTestId={`oopt-status-${project.short_name}`} className="flex justify-center">
                <TestableChip dataTestId={`oopt-release-status-${project.short_name}`} className={statusInfo.colour} value={statusInfo.description} />
              </TestableTableCell>
              <TestableTableCell dataTestId={`oopt-performance-${project.short_name}`}>
                {project.project_tags && Object.values(project.project_tags).flat().length > 0
                  ? (
                    <div className="flex flex-wrap gap-2">
                      {Object.values(project.project_tags).flat().map(tag => (
                        <TestableChip 
                          key={`${project.projectId}-${tag.id}`}
                          dataTestId={`oopt-tag-${tag.id}`}
                          className='bg-blue-500' 
                          value={tag.title}
                        />
                      ))}
                    </div>
                  )
                  : 'No tags'}
              </TestableTableCell>
              <TestableTableCell dataTestId={`oopt-action-${project.short_name}`}>
                <StyledTooltip content="View project details" placement="top">
                  <Link data-testid={`overview-project-${project.projectId}-action-link`} key={project.short_name} to={`/projects/${project.projectId}/releases/${latestRelease.release_id}`}>
                    <ArrowTopRightOnSquareIcon className="h-5 w-5 text-gray-800 dark:text-gray-100" />
                  </Link>
              </StyledTooltip>
              </TestableTableCell>
            </TestableTableRow>
            )
          }          )}
        </TestableTableBody>
      </TestableTable>
    </TestableCard>
  );
}

OrganisationOverviewProjectTable.propTypes = {
  projects: PropTypes.arrayOf(
    PropTypes.shape({
      short_name: PropTypes.string.isRequired,
      intro_text: PropTypes.string,
      releases: PropTypes.arrayOf(
        PropTypes.shape({
          version_id: PropTypes.string.isRequired,
          creation_date: PropTypes.string.isRequired,
          release_status: PropTypes.string.isRequired,
          release_id: PropTypes.number.isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
  isLoading: PropTypes.bool,
  };